import type { ReactNode } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Tooltip, Typography, theme } from 'antd';
import { CardContent, MetricCardEcho } from '@/components/cards/common';
import { PlatformIconEcho } from '@/components/PlatformIconEcho';
import { useFilterContext } from '@/context';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useUniqueViewersAndListenersQuery } from '@/services/revenueApi';
import { getDateString } from '@/utils/dateUtils/dateFormatUtils';
import { PODCAST_PLAYERS } from '@/utils';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import type { DateRange } from '@/types';
import type {
  UniqueMediaType,
  UniqueViewersAndListenersResponse,
} from '@/types/api/unique-viewers-and-listeners';

type DataType = UniqueViewersAndListenersResponse;

const DEFAULT_PLAYERS_LABEL = 'Podcast Players';

interface UniquesCardEchoProps {
  className?: string;
  label: string;
  tooltipContent?: ReactNode;
  podcastPlayersTooltipContent?: ReactNode;
  playersLabel?: string;
  playersOverride?: readonly string[];
  mediaType: UniqueMediaType;
}

interface UniquesCardDataProps {
  data?: DataType;
  selectedShow: string | undefined;
  mediaType: UniqueMediaType;
  label: string;
}

/**
 * Audience uniques metric card for a single metric (listeners, viewers, or both).
 */
const UniquesCardEcho = ({
  className,
  label,
  tooltipContent,
  podcastPlayersTooltipContent,
  playersLabel = DEFAULT_PLAYERS_LABEL,
  playersOverride,
  mediaType,
}: UniquesCardEchoProps): JSX.Element => {
  const { token } = theme.useToken();
  const { filters } = useFilterContext();

  const hasExactlyOneShow = filters.selectedShows.length === 1;
  const { queryResult } = useFetchQuery(useUniqueViewersAndListenersQuery, {
    // TODO: Remove this once we enable network level and multi show support on audience page
    enabled: hasExactlyOneShow,
  });

  const displayPlayers = playersOverride ?? PODCAST_PLAYERS;
  const isSinglePlayer = displayPlayers.length === 1;
  const showTooltipOnIcon = isSinglePlayer && !!podcastPlayersTooltipContent;
  const showMoreTrigger = !isSinglePlayer && !!podcastPlayersTooltipContent;

  const titleRow = (
    <Flex
      align="center"
      justify="space-between"
      wrap
      gap={token.marginXXS}
      style={{ minHeight: token.controlHeightSM }}
    >
      <Flex align="center" gap={token.marginXXS}>
        <Typography.Text style={{ color: token.colorTextSecondary }}>{label}</Typography.Text>
        {tooltipContent && (
          <Tooltip title={tooltipContent} placement="bottom">
            <InfoCircleOutlined
              aria-label={`${label} information`}
              style={{ color: token.colorTextSecondary, fontSize: token.fontSize }}
            />
          </Tooltip>
        )}
      </Flex>
      {displayPlayers.length > 0 && (
        <Flex
          align="center"
          gap={token.marginXXS}
          style={{ color: token.colorTextSecondary, fontSize: token.fontSize }}
        >
          <Typography.Text
            style={{
              marginInlineEnd: token.marginXXS,
              color: token.colorTextSecondary,
              fontSize: token.fontSizeSM,
              fontWeight: 300,
            }}
          >
            {playersLabel}
          </Typography.Text>
          {displayPlayers.map((appName: string) => (
            <Tooltip
              key={appName}
              title={showTooltipOnIcon ? podcastPlayersTooltipContent : appName}
              placement="bottom"
            >
              <Flex align="center">
                <PlatformIconEcho name={appName} />
              </Flex>
            </Tooltip>
          ))}
          {showMoreTrigger && (
            <Tooltip title={podcastPlayersTooltipContent} placement="bottom">
              <Typography.Text
                aria-label="Podcast players information"
                style={{ color: token.colorTextSecondary, cursor: 'default' }}
              >
                ....
              </Typography.Text>
            </Tooltip>
          )}
        </Flex>
      )}
    </Flex>
  );

  const footerText = getDateRangeFooterText(filters.dateRange);

  return (
    <MetricCardEcho
      className={className}
      dataTestId="unique-listeners-card-data"
      title={titleRow}
      footer={footerText}
      body={
        <CardContent
          variant="echo"
          {...queryResult}
          isPending={hasExactlyOneShow && queryResult.isPending}
          renderData={(data: DataType) => (
            <Data
              data={data}
              selectedShow={hasExactlyOneShow ? filters.selectedShows[0] : undefined}
              mediaType={mediaType}
              label={label}
            />
          )}
        />
      }
    />
  );
};

const getDateRangeFooterText = (selectedDateRange: DateRange): string => {
  const { to } = selectedDateRange;
  const from = new Date(to);
  from.setDate(from.getDate() - 7);

  const toDateString = getDateString(to);
  const fromDateString = getDateString(from.toISOString());

  return `7 days | ${fromDateString} - ${toDateString}`;
};

const Data = ({ data, selectedShow, mediaType, label }: UniquesCardDataProps): JSX.Element => {
  if (!data || selectedShow === undefined) {
    return <NoDataMessageEcho dataType={`${label.toLowerCase()} data`} />;
  }

  return (
    <Typography.Title level={3} ellipsis={{ rows: 1 }} style={{ margin: 0 }}>
      {data[mediaType].toLocaleString()}
    </Typography.Title>
  );
};

export default UniquesCardEcho;
